import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { alpha } from '@mui/material/styles';
import { Avatar, Box, Button, Grid, Link, Stack, Typography } from '@mui/material';
import EmailRoundedIcon from '@mui/icons-material/EmailRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import QuestionAnswerRoundedIcon from '@mui/icons-material/QuestionAnswerRounded';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';

import SupportImage from '../../Data/Images/SupportImage';
import { appURL } from '../../Data/baseURL';
import {
  BackToHome,
  GlassCard,
  PublicContainer,
  PublicPage,
  PublicPageHero,
  publicPalette,
} from '../../Public/PublicLayout';

const normalizePhoneHref = (value) => String(value ?? '').replace(/[^\d+]/g, '').replace(/^0+373/, '+373');

const Support = () => {
  const { t } = useTranslation();
  const phone = String(t('base.phone') ?? '');
  const email = String(t('base.email') ?? '');
  const skype = String(t('base.skype') ?? '');

  const contacts = useMemo(
    () => [
      { label: t('general.phone'), value: phone, href: `tel:${normalizePhoneHref(phone)}`, icon: <PhoneRoundedIcon />, color: publicPalette.accent },
      { label: t('general.email_capital'), value: email, href: `mailto:${email}`, icon: <EmailRoundedIcon />, color: publicPalette.primary },
      { label: 'Skype', value: skype, href: `skype:${skype}?chat`, icon: <QuestionAnswerRoundedIcon />, color: publicPalette.secondary },
    ],
    [email, phone, skype, t]
  );

  return (
    <PublicPage>
      <Helmet>
        <title>CONTDEPLATA — {t('general.support')}</title>
        <meta name="description" content={t('public.support_meta', { defaultValue: `Contact CONTDEPLATA support at ${email}.` })} />
      </Helmet>

      <PublicPageHero
        eyebrow={t('public.support_center', { defaultValue: 'Support center' })}
        title={t('public.support_title', { defaultValue: 'Human help when your workflow needs it' })}
        description={t('public.support_intro', { defaultValue: 'Get assistance with account setup, document creation, subscription questions, and day-to-day use of the platform.' })}
        actions={<BackToHome />}
        compact
      />

      <Box sx={{ py: { xs: 7, md: 11 }, backgroundColor: publicPalette.background }}>
        <PublicContainer>
          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} lg={7}>
              <GlassCard sx={{ height: '100%', p: { xs: 3, sm: 5 } }}>
                <Typography component="h2" sx={{ mb: 1.5, color: publicPalette.text, fontSize: { xs: 27, sm: 34 }, fontWeight: 900, letterSpacing: '-.035em' }}>
                  {t('public.contact_support', { defaultValue: 'Contact the support team' })}
                </Typography>
                <Typography sx={{ mb: 4, maxWidth: 650, color: publicPalette.muted, lineHeight: 1.75 }}>
                  {t('public.contact_support_text', { defaultValue: 'Choose the channel that is most convenient. Include your account email and a concise description of the issue so the team can identify the context quickly.' })}
                </Typography>

                <Grid container spacing={2}>
                  {contacts.map((contact) => (
                    <Grid item xs={12} sm={6} key={contact.label}>
                      <Link href={contact.href} underline="none">
                        <Box sx={{ height: '100%', p: 2.3, borderRadius: 3, border: `1px solid ${publicPalette.border}`, backgroundColor: alpha('#FFFFFF', 0.025), transition: 'transform 160ms ease, border-color 160ms ease', '&:hover': { transform: 'translateY(-3px)', borderColor: alpha(contact.color, 0.5) } }}>
                          <Avatar sx={{ mb: 1.5, color: contact.color, backgroundColor: alpha(contact.color, 0.1) }}>{contact.icon}</Avatar>
                          <Typography sx={{ color: publicPalette.muted, fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>{contact.label}</Typography>
                          <Typography sx={{ mt: 0.5, color: publicPalette.text, fontWeight: 800, overflowWrap: 'anywhere' }}>{contact.value}</Typography>
                        </Box>
                      </Link>
                    </Grid>
                  ))}
                  <Grid item xs={12} sm={6}>
                    <Box sx={{ height: '100%', p: 2.3, borderRadius: 3, border: `1px solid ${publicPalette.border}`, backgroundColor: alpha('#FFFFFF', 0.025) }}>
                      <Avatar sx={{ mb: 1.5, color: '#FBBF24', backgroundColor: alpha('#FBBF24', 0.1) }}><ScheduleRoundedIcon /></Avatar>
                      <Typography sx={{ color: publicPalette.muted, fontSize: 12, fontWeight: 800, letterSpacing: '.08em', textTransform: 'uppercase' }}>{t('public.response_time', { defaultValue: 'Response' })}</Typography>
                      <Typography sx={{ mt: 0.5, color: publicPalette.text, fontWeight: 800 }}>{t('public.business_hours', { defaultValue: 'During business hours' })}</Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Button href={`mailto:${email}`} sx={{ mt: 4, px: 3, py: 1.3, color: '#03111A', borderRadius: 2.5, textTransform: 'none', fontWeight: 900, background: `linear-gradient(110deg, ${publicPalette.primary}, ${publicPalette.accent})` }}>
                  {t('public.email_support', { defaultValue: 'Email support' })}
                </Button>
              </GlassCard>
            </Grid>

            <Grid item xs={12} lg={5}>
              <GlassCard sx={{ height: '100%', minHeight: 430, p: 3, display: 'grid', placeItems: 'center', overflow: 'hidden', background: `radial-gradient(circle at 50% 35%, ${alpha(publicPalette.secondary, 0.16)}, transparent 60%), ${publicPalette.surface}` }}>
                <Box sx={{ width: '100%', maxWidth: 520, '& svg': { width: '100%', height: 'auto', display: 'block' } }}>
                  <SupportImage />
                </Box>
              </GlassCard>
            </Grid>
          </Grid>

          <GlassCard sx={{ mt: 3, p: { xs: 3, md: 4 } }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between">
              <Box>
                <Typography sx={{ color: publicPalette.text, fontSize: 21, fontWeight: 900 }}>{t('public.check_faq', { defaultValue: 'Looking for a quick answer?' })}</Typography>
                <Typography sx={{ mt: 0.6, color: publicPalette.muted }}>{t('public.check_faq_text', { defaultValue: 'The FAQ covers account setup, invoice creation, bank details, users, and subscriptions.' })}</Typography>
              </Box>
              <Button href={`${appURL}/faq`} variant="outlined" sx={{ flexShrink: 0, color: publicPalette.text, borderColor: publicPalette.border, borderRadius: 2.5, textTransform: 'none', fontWeight: 850 }}>{t('faq.frequent_questions')}</Button>
            </Stack>
          </GlassCard>
        </PublicContainer>
      </Box>
    </PublicPage>
  );
};

export default Support;
